import React, { useState, useRef, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { BUNDLE_AVATAR_COLORS, BUNDLE_EASE } from "../../utils/variables/common"
import { getInitials } from "../../utils/parsers/common"
import { useTranslation } from "react-i18next"

const ITEM_HEIGHT = 44
const OVERSCAN = 5

function useVirtualList({ count, itemHeight, containerRef, overscan = OVERSCAN }) {
  const [scrollTop, setScrollTop] = useState(0)
  // FIX 1: начинаем с 0, не с 400 — чтобы не было прыжка при первом рендере
  const [containerHeight, setContainerHeight] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    // FIX 2: сразу читаем реальный размер до ResizeObserver
    setContainerHeight(el.clientHeight)

    const ro = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height)
    })
    ro.observe(el)

    const onScroll = () => setScrollTop(el.scrollTop)
    el.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      ro.disconnect()
      el.removeEventListener("scroll", onScroll)
    }
  }, [containerRef])

  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan)
  const visibleCount = Math.ceil(containerHeight / itemHeight) + overscan * 2
  const endIndex = Math.min(count - 1, startIndex + visibleCount)

  return { startIndex, endIndex, totalHeight: count * itemHeight }
}

const CustomerRow = React.memo(({ customer, index, colorIndex, style }) => (
  <div style={style} className="flex items-center gap-2.5 px-4 absolute w-full">
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
        BUNDLE_AVATAR_COLORS[colorIndex % BUNDLE_AVATAR_COLORS.length]
      }`}
    >
      {getInitials(customer.name)}
    </div>
    <div className="min-w-0 flex-1 flex items-center gap-2">
      <div className="text-sm font-medium text-gray-800 truncate">{customer.name}</div>
      <div className="text-[11px] text-gray-400 font-mono truncate shrink-0">
        {customer.passport} · {customer.dob}
      </div>
    </div>
  </div>
))

const CustomersPopup = ({ customers, onClose, isOpen }) => {
  console.log("----------------", isOpen)
  const { t } = useTranslation()

  const [query, setQuery] = useState("")
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => inputRef.current?.focus(), 150)
    return () => clearTimeout(timer)
  }, [isOpen])

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return customers
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.passport?.toLowerCase().includes(q) ||
        c.dob?.includes(q)
    )
  }, [customers, query])

  const { startIndex, endIndex, totalHeight } = useVirtualList({
    count: filtered.length,
    itemHeight: ITEM_HEIGHT,
    containerRef: scrollRef,
  })

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [query])

  useEffect(() => {
    if (!isOpen) setQuery("")
  }, [isOpen])

  const visibleItems = []
  for (let i = startIndex; i <= endIndex; i++) {
    visibleItems.push(
      <CustomerRow
        key={filtered[i].passport ?? i}
        customer={filtered[i]}
        index={i}
        colorIndex={i}
        style={{ top: i * ITEM_HEIGHT, height: ITEM_HEIGHT }}
      />
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-30 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={onClose}
          />

          <motion.div
            className="relative bg-white w-full sm:max-w-sm sm:rounded-xl shadow-2xl flex flex-col max-h-[90vh] rounded-t-2xl overflow-hidden"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.25, ease: BUNDLE_EASE }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">
                  {t("tourists.tourists")}
                </span>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 leading-none">
                  {filtered.length !== customers.length
                    ? `${filtered.length} / ${customers.length}`
                    : customers.length}
                </span>
              </div>
              <button
                onClick={onClose}
                className="w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-400"
                aria-label="Закрити"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search — показываем только когда список нетривиальный */}
            {customers.length > 8 && (
              <div className="px-4 py-2 border-b border-gray-100 shrink-0">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                    />
                  </svg>
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Пошук за ім'ям, паспортом…"
                    className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 placeholder-gray-400"
                  />
                  {query && (
                    <button
                      onClick={() => setQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Очистити"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div
              ref={scrollRef}
              className="overflow-y-auto flex-1"
              style={{ overscrollBehavior: "contain", willChange: "scroll-position" }}
            >
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <svg
                    className="w-8 h-8 mb-2 opacity-40"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0"
                    />
                  </svg>
                  <p className="text-sm">{t("admin.nothing_found")}</p>
                </div>
              ) : (
                <div className="relative" style={{ height: totalHeight }}>
                  {visibleItems}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-100 shrink-0">
              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                Закрити
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default CustomersPopup
